import { styled } from "@mui/material";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { useEffect, useState } from "react";

type Wobbliness = 1 | 2 | 3 | "1" | "2" | "3";
type ComponentToBeUsed =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "button"
  | "overline"
  | "inherit";

type WobblyTypographyProps = TypographyProps & {
  wobbliness?: Wobbliness;
  outlined?: boolean;
  component?: ComponentToBeUsed;
};

const generateTranslationValArray = (length: number, multiplier: number): Array<number> => {
  return Array.from(
    { length: length },
    () => Math.random() * multiplier * 0.1 * (Math.round(Math.random()) ? 1 : -1)
  );
};

const WobblyText = ({
  text,
  wobbliness,
}: {
  text: string;
  wobbliness: Wobbliness;
}): JSX.Element => {
  const multiplier = typeof wobbliness === "number" ? wobbliness : parseInt(wobbliness);
  const [translationVals, setTranslationVals] = useState<Array<number>>(
    generateTranslationValArray(text.length, multiplier)
  );
  useEffect(() => {
    setTranslationVals(generateTranslationValArray(text.length, multiplier));
  }, [text, multiplier]);
  const spans: Array<JSX.Element> = [];
  for (let i = 0; i < text.length; i++) {
    const extraWidth = text[i] === " " ? { width: "0.4em" } : null;
    spans.push(
      <span
        key={spans.length}
        style={{
          fontFamily: "Bungee",
          transform: translationVals[i] ? `translateY(${translationVals[i].toString()}em)` : "",
          ...extraWidth,
        }}
      >
        {text[i]}
      </span>
    );
  }
  return <>{spans}</>;
};

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "outlined",
})<TypographyProps & { outlined: boolean; component: ComponentToBeUsed }>(
  ({ outlined, theme }) => ({
    "& span": {
      display: "inline-block",
      letterSpacing: "-0.02em",
      WebkitTextStroke: outlined ? `0.04em ${theme.palette.text.primary}` : "none",
      color: outlined ? theme.palette.background.default : "inherit",
    },
  })
);

const WobblyTypography = ({
  wobbliness = 1,
  outlined = false,
  component,
  children,
  className,
  variant,
  ...rest
}: WobblyTypographyProps) => {
  if (typeof children !== "string") {
    console.error("WobblyTypography : Children need to be of type string");
    return <></>;
  }
  const textToWobble = children;
  return (
    <StyledTypography
      {...rest}
      variant={variant}
      component={component ? component : variant ? variant : "h1"}
      className={className ? `${className} WobblyTypography` : `WobblyTypography`}
      outlined={outlined}
    >
      <WobblyText text={textToWobble} wobbliness={wobbliness} />
    </StyledTypography>
  );
};

export default WobblyTypography;
